// Search, shared by the desktop's search box (App.tsx) and the phone's Find (PhoneSheets.tsx).
//
// Dan, 17 Oct 2026: "I tried searching 'Big Beautiful Bill' and nothing came up. We should include common names
// for items so they're searchable." Two things follow. An item is found by its other names in the record too
// (`Column.aka`, written by the build: Congress.gov's popular titles, other versions' and parts' short titles;
// never a name made up here). And a search that finds nothing in the term on screen looks in the other terms,
// whose grid files are loaded only then (and kept once loaded), so "Inflation Reduction" on 2025-2026 points to
// 2021-2022 instead of saying nothing.
import { useEffect, useState } from 'preact/hooks';
import { loadGrid, type Column, type Grid, type Meta, type Row, type Term } from './data';

/** An item's searchable words: its number, its title and its other names, apart so no match runs across two. */
export const itemText = (c: Column) => [c.number, c.title ?? '', ...(c.aka ?? [])].join(' | ').toLowerCase();

/** The other name a search found an item by, when its title alone did not match. */
export const foundAs = (c: Column, ql: string): string | null =>
  !ql || (c.title ?? '').toLowerCase().includes(ql) ? null : (c.aka ?? []).find((a) => a.toLowerCase().includes(ql)) ?? null;

/** A person's searchable words, as the desktop's box reads them. */
export const personText = (r: Row) => `${r.full} ${r.last} ${r.seat ?? ''}`.toLowerCase();

export type Elsewhere<T> = { term: Term; found: T };

/** What the other terms hold for a search the term on screen has nothing for: null while their files load,
 *  then each term with something (newest first). `find` reads one term's grid; `some` says it found anything. */
export function useElsewhere<T>(meta: Meta | null, congress: number | null, query: string,
                                find: (g: Grid) => T, some: (t: T) => boolean): Elsewhere<T>[] | null {
  const [out, setOut] = useState<Elsewhere<T>[] | null>(null);
  useEffect(() => {
    setOut(null);
    if (!meta || !query) return;
    let live = true;
    const others = meta.terms.filter((t) => t.congress !== congress);
    Promise.all(others.map((t) => loadGrid(t.grid).then((g) => ({ term: t, found: find(g) })).catch(() => null)))
      .then((all) => { if (live) setOut(all.filter((x): x is Elsewhere<T> => !!x && some(x.found))); });
    return () => { live = false; };
  }, [meta, congress, query]);
  return out;
}
